import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import ConfirmDialog from "../components/ConfirmDialog";
import SearchAndFilter from "../components/SearchAndFilter";
import LoadingSpinner from "../components/LoadingSpinner";
import api from "../services/api";
import { toast } from "react-toastify";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, searchTerm, dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);
      if (dateFilter) params.append("date", dateFilter);

      const response = await api.get(`/orders/admin/all?${params.toString()}`);

      if (response.data.success) {
        setOrders(response.data.orders);
      } else {
        toast.error("Failed to fetch orders");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ordered":
        return "success";
      case "pending":
        return "warning";
      case "processing":
        return "info";
      case "shipped":
        return "primary";
      case "delivered":
        return "success";
      case "cancelled":
        return "error";
      case "refunded":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      case "refunded":
        return "secondary";
      default:
        return "default";
    }
  };

  const handleViewOrder = async (order) => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/admin/${order.id}`);

      if (response.data.success) {
        setSelectedOrder(response.data.order);
        setOrderDialogOpen(true);
      } else {
        toast.error("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setTrackingNumber(order.trackingNumber || "");
    setOrderNotes(order.notes || "");
    setStatusDialogOpen(true);
  };

  const handleDownloadInvoice = async (order) => {
    try {
      const response = await api.get(`/orders/admin/${order.id}/invoice`, {
        responseType: "blob",
      });

      const invoiceId = (
        order.invoiceId || `invoice-${order.orderNumber || order.id}`
      ).toUpperCase();
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Invoice ${invoiceId} download started`);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const response = await api.put(
        `/orders/admin/${selectedOrder.id}/status`,
        {
          status: newStatus,
          trackingNumber: trackingNumber,
          notes: orderNotes,
        },
      );

      if (response.data.success) {
        setOrders((prev) =>
          prev.map((order) =>
            order.id === selectedOrder.id
              ? {
                  ...order,
                  status: newStatus,
                  updatedAt: new Date().toISOString(),
                }
              : order,
          ),
        );

        toast.success("Order status updated successfully");
        setStatusDialogOpen(false);
      } else {
        toast.error("Failed to update order status");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const handleCancelOrder = (order) => {
    setConfirmDialog({
      open: true,
      title: "Cancel Order",
      message: `Are you sure you want to cancel order ${order.orderNumber}? This will automatically process a refund if the order was paid.`,
      onConfirm: async () => {
        try {
          const response = await api.post(`/orders/admin/${order.id}/cancel`, {
            reason: "Order cancelled by admin",
          });

          if (response.data.success) {
            setOrders((prev) =>
              prev.map((o) =>
                o.id === order.id
                  ? {
                      ...o,
                      status: "cancelled",
                      paymentStatus: response.data.order.paymentStatus,
                      updatedAt: new Date().toISOString(),
                    }
                  : o,
              ),
            );

            toast.success("Order cancelled successfully");
          } else {
            toast.error(response.data.message || "Failed to cancel order");
          }
          setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          });
        } catch (error) {
          console.error("Error cancelling order:", error);
          toast.error(
            error.response?.data?.message || "Failed to cancel order",
          );
        }
      },
    });
  };

  const handleRefundOrder = (order) => {
    setConfirmDialog({
      open: true,
      title: "Process Refund",
      message: `Are you sure you want to refund order ${order.orderNumber} for ₹${order.total}? This will process a refund through the payment gateway.`,
      onConfirm: async () => {
        try {
          const response = await api.post(`/orders/admin/${order.id}/refund`, {
            refundType: "full",
            reason: "Refund processed by admin",
          });

          if (response.data.success) {
            setOrders((prev) =>
              prev.map((o) =>
                o.id === order.id
                  ? {
                      ...o,
                      status: "refunded",
                      paymentStatus: "refunded",
                      updatedAt: new Date().toISOString(),
                    }
                  : o,
              ),
            );

            toast.success(
              `Refund processed successfully. Refund ID: ${response.data.refund.refundId || "N/A"}`,
            );
          } else {
            toast.error(response.data.message || "Failed to process refund");
          }
          setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          });
        } catch (error) {
          console.error("Error processing refund:", error);
          toast.error(
            error.response?.data?.message || "Failed to process refund",
          );
        }
      },
    });
  };

  const handleExportOrders = () => {
    toast.success("Orders exported successfully");
  };

  const handlePrintInvoice = async (order) => {
    try {
      // Call the SAME invoice PDF API
      const response = await api.get(`/orders/admin/${order.id}/invoice`, {
        responseType: "blob",
      });

      // Create blob from PDF
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Open PDF in new tab (instead of downloading)
      const newWindow = window.open(url, "_blank");

      if (!newWindow) {
        toast.error("Popup blocked. Please allow popups to view invoice.");
        return;
      }

      toast.success(`Invoice ${order.orderNumber} opened`);

      // Optional: cleanup URL after some time
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Error opening invoice:", error);
      toast.error("Failed to open invoice");
    }
  };

  const generateInvoiceHTML = (order) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${order.orderNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .invoice-details { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .customer-info, .order-info { width: 45%; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .items-table th { background-color: #f2f2f2; }
          .total-section { text-align: right; margin-top: 20px; }
          .total-line { display: flex; justify-content: space-between; margin: 5px 0; }
          .grand-total { font-weight: bold; font-size: 1.2em; border-top: 2px solid #333; padding-top: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>GALA BEAUTY</h1>
          <h2>INVOICE</h2>
        </div>
        
        <div class="invoice-details">
          <div class="customer-info">
            <h3>Bill To:</h3>
            <p><strong>${order.customer.name}</strong></p>
            <p>${order.customer.email}</p>
            ${order.customer.phone ? `<p>${order.customer.phone}</p>` : ""}
            ${
              order.shippingAddress
                ? `
              <p>${order.shippingAddress.address}</p>
              <p>${order.shippingAddress.city}, ${order.shippingAddress.state} ${order.shippingAddress.zipCode}</p>
              <p>${order.shippingAddress.country}</p>
            `
                : ""
            }
          </div>
          
          <div class="order-info">
            <h3>Order Details:</h3>
            <p><strong>Order #:</strong> ${order.orderNumber}</p>
            <p><strong>Date:</strong> ${new Date(
              order.createdAt,
            ).toLocaleDateString("en-IN", {
              timeZone: "Asia/Kolkata",
            })}</p>
            <p><strong>Status:</strong> ${order.status}</p>
            <p><strong>Payment:</strong> ${order.paymentStatus}</p>
            ${order.trackingNumber ? `<p><strong>Tracking:</strong> ${order.trackingNumber}</p>` : ""}
          </div>
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Variant</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.items
              .map(
                (item) => `
              <tr>
                <td>${item.product?.name || item.name}</td>
                <td>${item.variant ? item.variant.name || "Default" : "Default"}</td>
                <td>${item.quantity}</td>
                <td>₹${(item.price || 0).toFixed(2)}</td>
                <td>₹${(item.total || 0).toFixed(2)}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
        
        <div class="total-section">
          <div class="total-line">
            <span>Subtotal:</span>
            <span>₹${(order.subtotal || order.items?.reduce((sum, item) => sum + (item.total || 0), 0) || 0).toFixed(2)}</span>
          </div>
          ${
            order.discount > 0
              ? `
            <div class="total-line">
              <span>Discount:</span>
              <span>-₹${(order.discount || 0).toFixed(2)}</span>
            </div>
          `
              : ""
          }
          ${
            order.couponCode
              ? `
            <div class="total-line">
              <span>Coupon (${order.couponCode}):</span>
              <span>Applied</span>
            </div>
          `
              : ""
          }
          <div class="total-line grand-total">
            <span>Total:</span>
            <span>₹${(order.grandTotal || order.total || 0).toFixed(2)}</span>
          </div>
        </div>
        
        ${
          order.notes
            ? `
          <div style="margin-top: 30px;">
            <h3>Notes:</h3>
            <p>${order.notes}</p>
          </div>
        `
            : ""
        }
      </body>
      </html>
    `;
  };

  const columns = [
    {
      field: "orderNumber",
      headerName: "Order #",
      width: 150,
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "customer",
      headerName: "Customer",
      width: 250,
      flex: 1.2,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.value.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "total",
      headerName: "Total",
      width: 120,
      flex: 0.6,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          ₹{(params.value || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "paymentStatus",
      headerName: "Payment",
      width: 130,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getPaymentStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 150,
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata",
          })}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewOrder(params.row)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Update Status">
            <IconButton
              size="small"
              onClick={() => handleUpdateStatus(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Invoice">
            <IconButton
              size="small"
              onClick={() => handleDownloadInvoice(params.row)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print/PDF Invoice">
            <IconButton
              size="small"
              onClick={() => handlePrintInvoice(params.row)}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          {/* {params.row.status !== 'cancelled' && params.row.status !== 'refunded' && (
            <Tooltip title="Cancel Order">
              <IconButton size="small" onClick={() => handleCancelOrder(params.row)}>
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )} */}
          {params.row.status === "delivered" &&
            params.row.paymentStatus === "paid" && (
              <Tooltip title="Process Refund">
                <IconButton
                  size="small"
                  onClick={() => handleRefundOrder(params.row)}
                >
                  <MoneyIcon />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      ),
    },
  ];

  const filters = [
    {
      key: "status",
      label: "Status",
      value: statusFilter,
      options: [
        { value: "", label: "All Status" },
        { value: "ordered", label: "Ordered" },
        { value: "pending", label: "Pending" },
        { value: "processing", label: "Processing" },
        { value: "shipped", label: "Shipped" },
        { value: "delivered", label: "Delivered" },
        { value: "cancelled", label: "Cancelled" },
        { value: "refunded", label: "Refunded" },
      ],
    },
    {
      key: "date",
      label: "Date Range",
      value: dateFilter,
      options: [
        { value: "", label: "All Dates" },
        { value: "today", label: "Today" },
        { value: "week", label: "This Week" },
        { value: "month", label: "This Month" },
        { value: "year", label: "This Year" },
      ],
    },
  ];

  const handleFilterChange = (key, value) => {
    if (key === "status") setStatusFilter(value);
    if (key === "date") setDateFilter(value);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setDateFilter("");
  };

  // Check if any filters are applied
  const hasActiveFilters = statusFilter || dateFilter || searchTerm;

  if (loading) {
    return <LoadingSpinner message="Loading orders..." />;
  }

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Order Management
      </Typography>

      {/* Search and Filters - Always show */}
      <SearchAndFilter
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search orders, customers, or order numbers..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Clear Filters Button - Only show when filters are applied */}
      {hasActiveFilters && (
        <Box mb={2}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handleClearFilters}
            sx={{ mb: 2 }}
          >
            Clear Filters
          </Button>
        </Box>
      )}

      {/* Action Buttons */}
      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchOrders}
        >
          Refresh
        </Button>
      </Box>

      {/* Orders Table */}
      <Card sx={{ width: "100%" }}>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={orders}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              border: 0,
              width: "100%",
              height: "calc(100vh - 300px)",
              "& .MuiDataGrid-root": {
                width: "100%",
                height: "100%",
              },
              "& .MuiDataGrid-main": {
                width: "100%",
                height: "100%",
              },
              "& .MuiDataGrid-virtualScroller": {
                width: "100%",
                height: "100%",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5">
              Order Details - {selectedOrder?.orderNumber}
            </Typography>
            <Box display="flex" gap={1}>
              <Chip
                label={selectedOrder?.status}
                color={getStatusColor(selectedOrder?.status)}
                size="small"
              />
              <Chip
                label={selectedOrder?.paymentStatus}
                color={getPaymentStatusColor(selectedOrder?.paymentStatus)}
                size="small"
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Customer Information
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={selectedOrder.customer.name}
                          secondary={selectedOrder.customer.email}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Phone"
                          secondary={
                            selectedOrder.customer.phone || "Not provided"
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Order Information
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <ShoppingCartIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Order Status"
                          secondary={selectedOrder.status}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <PaymentIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Payment Status"
                          secondary={selectedOrder.paymentStatus}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ShippingIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Tracking Number"
                          secondary={
                            selectedOrder.trackingNumber || "Not assigned"
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Order Date"
                          secondary={new Date(
                            selectedOrder.createdAt,
                          ).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Shipping Address
                    </Typography>
                    {selectedOrder.shippingAddress ? (
                      <Box>
                        <Typography variant="body1">
                          {selectedOrder.shippingAddress.firstName}{" "}
                          {selectedOrder.shippingAddress.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.shippingAddress.address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.shippingAddress.city},{" "}
                          {selectedOrder.shippingAddress.state}{" "}
                          {selectedOrder.shippingAddress.zipCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.shippingAddress.country}
                        </Typography>
                        {selectedOrder.shippingAddress.phone && (
                          <Typography variant="body2" color="text.secondary">
                            Phone: {selectedOrder.shippingAddress.phone}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">
                        No shipping address provided
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Billing Address
                    </Typography>
                    {selectedOrder.billingAddress ? (
                      <Box>
                        <Typography variant="body1">
                          {selectedOrder.billingAddress.firstName}{" "}
                          {selectedOrder.billingAddress.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.billingAddress.address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.billingAddress.city},{" "}
                          {selectedOrder.billingAddress.state}{" "}
                          {selectedOrder.billingAddress.zipCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.billingAddress.country}
                        </Typography>
                        {selectedOrder.billingAddress.phone && (
                          <Typography variant="body2" color="text.secondary">
                            Phone: {selectedOrder.billingAddress.phone}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">
                        Same as shipping address
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Order Items */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Order Items
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Variant</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {item.product?.name || item.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {item.variant ? (
                                  <Box>
                                    {item.variant.name && (
                                      <Typography variant="body2">
                                        {item.variant.name}
                                      </Typography>
                                    )}
                                    {item.variant.attributes &&
                                      Object.keys(item.variant.attributes)
                                        .length > 0 && (
                                        <Box>
                                          {Object.entries(
                                            item.variant.attributes,
                                          ).map(([key, value]) => (
                                            <Typography
                                              key={key}
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {key}: {value}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Default
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {item.quantity}
                              </TableCell>
                              <TableCell align="right">
                                ₹{(item.price || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                ₹{(item.total || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Order Summary */}
                    <Box mt={3}>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" mt={2}>
                        <Typography variant="body1">Subtotal:</Typography>
                        <Typography variant="body1">
                          ₹{(selectedOrder.subtotal || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      {selectedOrder.discount > 0 && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body1">Discount:</Typography>
                          <Typography variant="body1" color="success.main">
                            -₹{(selectedOrder.discount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {selectedOrder.couponCode && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Coupon: {selectedOrder.couponCode}
                          </Typography>
                        </Box>
                      )}
                      <Divider />
                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography variant="h6">Total:</Typography>
                        <Typography variant="h6">
                          ₹{(selectedOrder.grandTotal || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Order Notes */}
                    {selectedOrder.notes && (
                      <Box mt={3}>
                        <Typography variant="h6" gutterBottom>
                          Order Notes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            onClick={() => handleUpdateStatus(selectedOrder)}
            startIcon={<EditIcon />}
          >
            Update Status
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePrintInvoice(selectedOrder)}
          >
            Print Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Tracking Number"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Enter tracking number (optional)"
          />

          <TextField
            fullWidth
            label="Order Notes"
            value={orderNotes}
            onChange={(e) => setOrderNotes(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            placeholder="Add notes about this order (optional)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusUpdate}>
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          })
        }
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
    </Box>
  );
};

export default Orders;
